import { useCallback, useEffect, useMemo, useState } from "react";

import { Button, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";

import { useOutletApi } from "@/features/authentication";
import {
  DEFAULT_VALUES,
  ExpenseAccrualForm,
} from "@/features/expense-accruals";
import { getErrorMessage } from "@/features/shared";
import { CenteredLoading, PageContainer, useSnackbar } from "@/features/ui";

export const EditExpenseAccrual = () => {
  const { referenceNumber } = useParams();
  const navigate = useNavigate();
  const outletApi = useOutletApi();
  const { showSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expenseAccrual, setExpenseAccrual] = useState(null);
  const [error, setError] = useState(null);

  const loadExpenseAccrual = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const {
        data: { data },
      } = await outletApi.get(`expense-accruals/${referenceNumber}`);
      setExpenseAccrual(data);
    } catch (error) {
      setError(getErrorMessage(error, "Gagal memuat pencatatan beban"));
    } finally {
      setLoading(false);
    }
  }, [outletApi, referenceNumber]);

  useEffect(() => {
    loadExpenseAccrual();
  }, [loadExpenseAccrual]);

  const defaultValues = useMemo(() => {
    if (!expenseAccrual) return DEFAULT_VALUES;

    return {
      transactionDate: dayjs(expenseAccrual.transactionDate),
      dueDate: dayjs(expenseAccrual.dueDate),
      expensePayableOutletChartOfAccount:
        expenseAccrual.expensePayableOutletChartOfAccount || null,
      note: expenseAccrual.note || "",
      expenseAccrualDetails: (expenseAccrual.expenseAccrualDetails || []).map(
        (line) => ({
          outletChartOfAccount: line.outletChartOfAccount || null,
          amount: line.amount,
          note: line.note || "",
        })
      ),
    };
  }, [expenseAccrual]);

  const handleSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      const { outletId, ...payload } = data;

      await outletApi.put(`expense-accruals/${referenceNumber}`, payload, {
        headers: { "Outlet-Id": outletId },
      });

      showSnackbar("Pencatatan beban berhasil diperbarui", "success");
      navigate(`/expense/expense-accruals/${referenceNumber}`);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memperbarui pencatatan beban"));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <CenteredLoading sx={{ height: "100vh" }} />;
  }

  if (error) {
    return (
      <Typography variant="h6" color="error">
        {error}
      </Typography>
    );
  }

  return (
    <PageContainer
      maxWidth="xl"
      title="Ubah Pencatatan Beban"
      breadcrumbs={[
        { title: "Pencatatan Beban", path: "/expense/expense-accruals" },
        {
          title: referenceNumber,
          path: `/expense/expense-accruals/${referenceNumber}`,
        },
        { title: "Ubah" },
      ]}
    >
      <ExpenseAccrualForm
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        editMode
        renderActionButton={() => (
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            size="large"
          >
            {isSubmitting ? "Menyimpan..." : "Simpan"}
          </Button>
        )}
      />
    </PageContainer>
  );
};
